import { FaBriefcase, FaExternalLinkAlt, FaCertificate } from "react-icons/fa";
import TitleHeader from "../../general/TitleHeader";
import { Fade } from "react-awesome-reveal";
import { Link } from "react-router-dom";

const experienceData = [
  {
    id: "depi",
    title: "Digital Egypt Pioneers Initiative",
    role: "Team Leader",
    period: "Apr 2024 - Oct 2024",
    image: {
      src: "imgs/depiEN.png",
      alt: "DEPI Logo",
    },
    description:
      "Completed the Digital Egypt Pioneers Program sponsored by the Ministry of Communications and Information Technology (MCIT) of Egypt, demonstrating leadership, teamwork, and problem-solving skills.",
    certificates: [
      {
        label: "DEPI Certificate",
        link: "https://drive.google.com/file/d/1tTsHY5-cyAJ0JR-3qiwdABFP82P2JQy0/view?usp=drive_link",
        type: "certificate",
      },
      {
        label: "Leadership Certificate",
        link: "https://drive.google.com/file/d/1bf4ZTIo7_sa9fyr5OHhPV1pZNIGHaeGc/view?usp=drive_link",
        type: "leadership",
      },
    ],
    website: "https://depi.gov.eg",
  },
  {
    id: "eramo",
    title: "E-RAMO",
    role: "Front-End Developer",
    period: "Aug 2024 - Present",
    image: {
      src: "imgs/eramo_logo.jpg",
      alt: "E-RAMO Logo",
    },
    projects: [
      {
        name: "Amtalek",
        description: "Multi-vendor real estate platform",
        highlights: [
          "Connecting leads with vendors via robust system",
          "Built with Next.js, server actions, React Form, and Redux",
          "SEO optimization and dynamic sitemap implementation",
        ],
        link: "https://amtalek.com",
        technologies: ["Next.js", "React", "Redux", "SEO"],
      },
      {
        name: "Lanacrm Website",
        description: "Real-Estate Single Vendor Website",
        highlights: [
          "Enhanced features and fixed critical software issues",
          "Improved Arabic/English translations",
          "Enhanced responsive design for all devices",
        ],
        link: "https://demo.lanacrm.com/ar",
        technologies: ["React", "CSS", "i18n"],
      },
    ],
  },
  {
    id: "freelance",
    title: "Freelance Projects",
    role: "Front-End Developer",
    period: "2023 - Present",
    icon: <FaBriefcase className="text-2xl text-blue-600" />,
    projects: [
      {
        name: "E-Commerce - Ayo 7 Store",
        description: "Modern e-commerce platform",
        highlights: [
          "Front-end e-commerce with Next.js and Tailwind CSS",
          "UI components with ShadCN UI",
          "Vercel deployment and optimization",
        ],
        link: "https://ayo-7.netlify.app/en",
        technologies: ["Next.js", "Tailwind", "ShadCN UI", "Vercel"],
      },
      {
        name: "Data Analyst Portfolio",
        description: "Interactive portfolio website",
        highlights: [
          "Responsive design with React and React-Slick",
          "Medium API integration for dynamic content",
          "Performance optimization and lazy loading",
        ],
        link: "https://bassam-portfolio.netlify.app/",
        technologies: ["React", "Context API", "Medium API"],
      },
      {
        name: "Tresor Café",
        description: "Cafe website with payment integration",
        highlights: [
          "Interactive food and drink menu",
          "Instapay payment integration",
          "QR code WiFi connection feature",
        ],
        link: "https://treasor-menu.netlify.app/",
        technologies: ["HTML", "CSS", "JavaScript", "Instapay"],
      },
    ],
  },
];

const ExperienceHeader = ({ experience }) => (
  <div className="flex items-start mb-6">
    {experience.image ? (
      <img
        src={experience.image.src}
        alt={experience.image.alt}
        loading="lazy"
        className="w-16 h-16 rounded-lg object-contain mr-4 border border-gray-200"
      />
    ) : experience.icon ? (
      <div className="w-16 h-16 rounded-lg bg-blue-50 flex items-center justify-center mr-4 border border-blue-100">
        {experience.icon}
      </div>
    ) : (
      <div className="w-16 h-16 rounded-lg bg-gray-100 flex items-center justify-center mr-4 border border-gray-200">
        <FaBriefcase className="text-xl text-gray-600" />
      </div>
    )}

    <div className="flex-1">
      <h3 className="text-xl font-bold text-gray-900 mb-1">
        {experience.title}
      </h3>
      <p className="text-gray-700 font-medium text-sm mb-1">
        {experience.role}
      </p>
      {experience.period && (
        <p className="text-gray-500 text-xs bg-gray-50 px-2 py-1 rounded-full inline-block">
          {experience.period}
        </p>
      )}
    </div>
  </div>
);

const ProjectCard = ({ project }) => (
  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:border-blue-300 transition-colors duration-200">
    <div className="flex justify-between items-start mb-3">
      <h5 className="font-semibold text-gray-900 text-sm">{project.name}</h5>
      {project.technologies && (
        <div className="flex flex-wrap gap-1">
          {project.technologies.slice(0, 2).map((tech, index) => (
            <span
              key={index}
              className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
            >
              {tech}
            </span>
          ))}
        </div>
      )}
    </div>

    {project.description && (
      <p className="text-gray-600 text-sm mb-3">{project.description}</p>
    )}

    <ul className="space-y-2 mb-3">
      {project.highlights.map((highlight, index) => (
        <li key={index} className="flex items-start text-sm text-gray-700">
          <span className="text-blue-500 mr-2 mt-1">•</span>
          {highlight}
        </li>
      ))}
    </ul>

    {project.link && project.link !== "#" && (
      <Link
        to={project.link}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center text-blue-600 hover:text-blue-800 font-medium text-sm transition-colors duration-200"
      >
        View Project <FaExternalLinkAlt className="ml-1 text-xs" />
      </Link>
    )}
  </div>
);

const CertificateSection = ({ certificates }) => (
  <div className="mt-6 pt-4 border-t border-gray-200">
    <h4 className="flex items-center text-lg font-semibold text-gray-900 mb-3">
      <FaCertificate className="mr-2 text-blue-600" />
      Certificates
    </h4>
    <div className="space-y-2">
      {certificates.map((cert, index) => (
        <Link
          key={index}
          to={cert.link}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center text-gray-700 hover:text-blue-600 text-sm transition-colors duration-200"
        >
          <FaCertificate className="mr-2 text-green-500 text-xs" />
          {cert.label}
        </Link>
      ))}
    </div>
  </div>
);

const ExperienceCard = ({ experience }) => (
  <Fade cascade damping={0.1} triggerOnce className="">
    <div className="group bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 p-6 border border-gray-200 hover:border-blue-200 space-y-3">
      <ExperienceHeader experience={experience} />

      {experience.description && (
        <p className="text-gray-700 text-sm leading-relaxed mb-6 bg-blue-50 p-4 rounded-lg border border-blue-100">
          {experience.description}
        </p>
      )}

      {experience.projects && (
        <div className="mt-4">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 pb-2 border-b border-gray-200">
            Projects
          </h4>
          <div className="space-y-4">
            {experience.projects.map((project, index) => (
              <ProjectCard key={index} project={project} />
            ))}
          </div>
        </div>
      )}

      {experience.certificates && (
        <CertificateSection certificates={experience.certificates} />
      )}

      {experience.website && experience.website !== "#" && (
        <Link
          to={experience.website}
          target="_blank"
          rel="noopener noreferrer"
          className="sp-button-components !flex !w-full justify-center items-center"
          // className="mt-6 flex items-center justify-center w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2 px-4 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium text-sm group"
        >
          Visit {experience.title}
          <FaExternalLinkAlt className="ml-2 text-xs group-hover:translate-x-1 transition-transform duration-200" />
        </Link>
      )}
    </div>
  </Fade>
);

export default function Experience() {
  return (
    <section id="experience" className="bg-dark-forest py-20">
      <div className="container">
        <Fade triggerOnce>
          <TitleHeader
            title="Professional Experience"
            subtitle="My journey and projects across different roles"
            icon={<FaBriefcase />}
          />
        </Fade>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-1 xl:grid-cols-3 gap-6">
          {experienceData.map((experience) => (
            <ExperienceCard key={experience.id} experience={experience} />
          ))}
        </div>
      </div>
    </section>
  );
}
// import { FaBriefcase } from "react-icons/fa";
// import TitleHeader from "../../general/TitleHeader";
// import { Fade } from "react-awesome-reveal";
// import { Link } from "react-router-dom";

// // const experiences = [
// //   {
// //     id: "eramo",
// //     title: "E-RAMO",
// //     role: "Front-End Developer",
// //     period: "Aug 2024 - Present",
// //     image: "imgs/eramo_logo.jpg",
// //     // description: `Established in 2010 in Egypt, e-RAMO provides high-quality web development and design services. The company focuses on delivering exceptional websites that stand out and are optimized for search engines.`,
// //     projects: [
// //       {
// //         name: "Amtalek",
// //         items: [
// //           "Multi-vendor real estate platform connecting leads with vendors via a robust system.",
// //           "Built using Next.js with server actions, React Form, and Redux.",
// //           "Implemented SEO optimization and generated dynamic sitemap.",
// //         ],
// //         link: "https://amtalek.com",
// //       },
// //       {
// //         name: "Lanacrm Website",
// //         items: [
// //           "Real-Estate Single Vendor Website.",
// //           "Added some features to the site, Fixed a number of software issues that were affecting the site.",
// //           "Arabic and English translations Fixed.",
// //           "Improved compatibility with different screens to ensure a smooth experience on phones and tablets.",
// //         ],
// //         link: "https://demo.lanacrm.com/ar",
// //       },
// //     ],
// //     // website: "https://www.e-ramo.net",
// //   },
// //   {
// //     id: "Freelance Projects",
// //     title: "Freelance Projects",
// //     role: "Front-End Developer",
// //     period: "- - Present",
// //     // image: "imgs/eramo_logo.jpg",
// //     // description: `Established in 2010 in Egypt, e-RAMO provides high-quality web development and design services. The company focuses on delivering exceptional websites that stand out and are optimized for search engines.`,
// //     projects: [
// //       {
// //         name: "E-Commerce - Ayo 7 Store",
// //         items: [
// //           "A Front-End e-commerce developed using Next.js, Tailwind CSS, and ShadCN UI, hosted on Vercel.",
// //         ],
// //         link: "https://store-smoky-two.vercel.app/",
// //       },
// //       {
// //         name: "Data Analyst - Portfolio",
// //         items: [
// //           "I built and designed a responsive, interactive portfolio using React, React-Slick, and the Context API.",
// //           "The Medium API is integrated for dynamic content, improved performance, and image loading.",
// //         ],
// //         link: "https://bassam-portfolio.netlify.app/",
// //       },
// //       {
// //         name: "Tresor Café",
// //         items: [
// //           "Tresor Café website, developed using (HTML, CSS, JavaScript)",
// //           "A responsive web application designed for Tresor Cafe.",
// //           "The website displays an interactive food and drink menu and integrates the Instapay payment solution for customers who prefer not to carry cash. Additionally, it provides a dedicated QR code for instant WiFi connection",
// //         ],
// //         link: "https://store-smoky-two.vercel.app/",
// //       },
// //     ],
// //   },
// //   {
// //     id: "depi",
// //     title: "Digital Egypt Pioneers Initiative - DEPI",
// //     role: "Team Leader (Digital Egypt Pioneers Program)",
// //     period: "Apr 2024 - Oct 2024",
// //     image: "imgs/depiEN.png",
// //     description: `Completed the Digital Egypt Pioneers Program sponsored by the Ministry of Communications and Information Technology (MCIT) of Egypt, demonstrating leadership and problem-solving skills.`,
// //     certificate: {
// //       label: "View Certificate",
// //       link: "pdf/leadership_depi.pdf",
// //     },
// //     website: "https://depi.gov.eg",
// //   },
// // ];

// const experiences = [
//   {
//     id: "eramo",
//     title: "E-RAMO",
//     role: "Front-End Developer",
//     period: "Aug 2024 - Present",
//     image: "imgs/eramo_logo.jpg",
//     projects: [
//       {
//         name: "Amtalek",
//         items: [
//           "Multi-vendor real estate platform connecting leads with vendors via a robust system.",
//           "Built using Next.js with server actions, React Form, and Redux.",
//           "Implemented SEO optimization and generated dynamic sitemap.",
//         ],
//         link: "https://amtalek.com",
//       },
//       {
//         name: "Lanacrm Website",
//         items: [
//           "Real-Estate Single Vendor Website.",
//           "Added some features to the site, Fixed a number of software issues that were affecting the site.",
//           "Arabic and English translations Fixed.",
//           "Improved compatibility with different screens to ensure a smooth experience on phones and tablets.",
//         ],
//         link: "https://demo.lanacrm.com/ar",
//       },
//     ],
//   },
//   {
//     id: "Freelance Projects",
//     title: "Freelance Projects",
//     role: "Front-End Developer",
//     period: "- - Present",
//     projects: [
//       {
//         name: "E-Commerce - Ayo 7 Store",
//         items: [
//           "A Front-End e-commerce developed using Next.js, Tailwind CSS, and ShadCN UI, hosted on Vercel.",
//         ],
//         link: "https://store-smoky-two.vercel.app/",
//       },
//       {
//         name: "Data Analyst - Portfolio",
//         items: [
//           "I built and designed a responsive, interactive portfolio using React, React-Slick, and the Context API.",
//           "The Medium API is integrated for dynamic content, improved performance, and image loading.",
//         ],
//         link: "https://bassam-portfolio.netlify.app/",
//       },
//       {
//         name: "Tresor Café",
//         items: [
//           "Tresor Café website, developed using (HTML, CSS, JavaScript)",
//           "A responsive web application designed for Tresor Cafe.",
//           "The website displays an interactive food and drink menu and integrates the Instapay payment solution for customers who prefer not to carry cash. Additionally, it provides a dedicated QR code for instant WiFi connection",
//         ],
//         link: "https://store-smoky-two.vercel.app/",
//       },
//     ],
//   },
//   {
//     id: "depi",
//     title: "Digital Egypt Pioneers Initiative - DEPI",
//     role: "Team Leader (Digital Egypt Pioneers Program)",
//     period: "Apr 2024 - Oct 2024",
//     image: "imgs/depiEN.png",
//     description: `Completed the Digital Egypt Pioneers Program sponsored by the Ministry of Communications and Information Technology (MCIT) of Egypt, demonstrating leadership, teamwork, and problem-solving skills.`,
//     certificates: [
//       {
//         label: "View DEPI Certificate",
//         link: "pdf/depi_certificates.pdf",
//       },
//       {
//         label: "View Leadership Certificate",
//         link: "pdf/leadership_depi.pdf",
//       },
//     ],
//     website: "https://depi.gov.eg",
//   },
// ];

// export default function Experience() {
//   return (
//     // mt-16 bg-customBlue
//     <section id="experience" className="py-24 bg-dark-forest">
//       <Fade>
//         <TitleHeader title="My Experience" icon={<FaBriefcase />} />
//         <div className="container grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
//           {experiences.map((exp) => (
//             <div
//               key={exp.id}
//               // shadow-lg
//               className="rounded-lg drop-shadow-lg bg-white p-6 border"
//             >
//               <div className="flex items-center mb-4">
//                 {exp.image && (
//                   <img
//                     src={exp.image}
//                     alt={`${exp.title} Logo`}
//                     loading="lazy"
//                     className="w-16 h-16 mr-4"
//                   />
//                 )}
//                 <div>
//                   <h3 className="text-xl font-semibold text-gray-800">
//                     {exp.title}
//                   </h3>
//                   <p className="text-gray-600 text-sm">{exp.role}</p>
//                   {exp.period && (
//                     <p className="text-gray-500 text-xs">{exp.period}</p>
//                   )}
//                 </div>
//               </div>
//               {exp.description && (
//                 <p className="text-gray-700 mb-4">{exp.description}</p>
//               )}
//               {exp.projects && (
//                 <div className="mt-4">
//                   <h4 className="text-lg font-semibold text-gray-800 mb-2">
//                     Projects:
//                   </h4>
//                   <ul className="space-y-4">
//                     {exp.projects.map((proj, idx) => (
//                       <li key={idx}>
//                         <h5 className="text-gray-800 font-medium">
//                           {proj.name}
//                         </h5>
//                         <ul className="list-disc list-inside text-gray-600 text-sm mb-1">
//                           {proj.items.map((item, i) => (
//                             <li key={i}>{item}</li>
//                           ))}
//                         </ul>
//                         {proj.link && proj.link !== "#" && (
//                           <Link
//                             to={proj.link}
//                             target="_blank"
//                             rel="noopener noreferrer"
//                             className="text-blue-500 hover:underline text-sm"
//                           >
//                             View Project
//                           </Link>
//                         )}
//                       </li>
//                     ))}
//                   </ul>
//                 </div>
//               )}
//               {exp.certificates && (
//                 <div className="mt-4">
//                   <h4 className="text-lg font-semibold text-gray-800 mb-2">
//                     Certificate:
//                   </h4>
//                   <Link
//                     to={exp.certificates.link}
//                     target="_blank"
//                     rel="noopener noreferrer"
//                     className="text-blue-500 hover:underline text-sm"
//                   >
//                     {exp.certificates.label}
//                   </Link>
//                 </div>
//               )}
//               {exp.website && exp.website !== "#" && (
//                 <Link
//                   to={exp.website}
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="block mt-6 text-blue-500 hover:underline text-sm"
//                 >
//                   {`Visit ${exp.title}`}
//                 </Link>
//               )}
//             </div>
//           ))}
//         </div>
//       </Fade>
//     </section>
//   );
// }
